/**
 * Auth utilities for Foresight Meet Schedule Predictor (Supabase Auth).
 *
 * Flow (native only — web is view-only):
 *   1. startGoogleLogin() asks Supabase for the Google OAuth URL with
 *      redirectTo = https://app.foresightpulse.com/auth-callback, then
 *      opens it in the system browser via @capacitor/browser.
 *   2. User signs in with Google. Supabase redirects to our app-link callback
 *      with #access_token=…&refresh_token=… in the URL fragment.
 *   3. Android App Links re-open our app and fire the @capacitor/app
 *      `appUrlOpen` event (handled in App.js). The handler parses the tokens
 *      and calls applyOAuthRedirect() below, which feeds them to
 *      supabase.auth.setSession() and persists them via the Capacitor
 *      Preferences-backed storage adapter.
 *   4. Subsequent API calls read the Supabase access_token via
 *      getSessionToken() (mirrored into the legacy `foresight_session_token`
 *      localStorage key so api.js stays untouched).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS
 * BREAKS THE AUTH. The redirect URL must stay in sync with the App Link
 * verified at https://app.foresightpulse.com/.well-known/assetlinks.json.
 */
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import { supabase } from './supabaseClient';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const NATIVE_REDIRECT_BASE =
  process.env.REACT_APP_PUBLIC_WEB_URL || 'https://app.foresightpulse.com';
const REDIRECT_URL = `${NATIVE_REDIRECT_BASE}/auth-callback`;

// Legacy key that api.js + Sentry already read for the Bearer fallback.
// We mirror Supabase's access_token into it on every session change so the
// rest of the app keeps working without further edits.
const LEGACY_TOKEN_KEY = 'foresight_session_token';
const LEGACY_USER_KEY = 'foresight_user';

const safeSet = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (_) {
    /* quota / private-mode — non-fatal */
  }
};

const mirrorSessionToLegacy = (session) => {
  if (!session) {
    safeSet(LEGACY_TOKEN_KEY, null);
    safeSet(LEGACY_USER_KEY, null);
    return;
  }
  safeSet(LEGACY_TOKEN_KEY, session.access_token || null);
  const u = session.user || {};
  const meta = u.user_metadata || {};
  safeSet(
    LEGACY_USER_KEY,
    JSON.stringify({
      user_id: u.id,
      email: u.email || meta.email || '',
      name: meta.full_name || meta.name || '',
      picture: meta.avatar_url || meta.picture || null,
    }),
  );
};

// Keep the legacy mirror in lockstep with Supabase. Token refresh fires
// onAuthStateChange too, so this also keeps long-running sessions usable.
supabase.auth.onAuthStateChange((_event, session) => {
  mirrorSessionToLegacy(session);
});
// And on import, hydrate from whatever Supabase already has cached.
supabase.auth.getSession().then(({ data }) => {
  if (data?.session) mirrorSessionToLegacy(data.session);
});

/**
 * Kick off Google sign-in.
 * Native: opens the Supabase-supplied Google OAuth URL in the system browser.
 * Web: not used (web is view-only) but kept for completeness — falls back to
 *      a full-page redirect just in case.
 */
export const startGoogleLogin = async () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: REDIRECT_URL,
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  const url = data?.url;
  if (!url) throw new Error('Supabase did not return an OAuth URL');

  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url, windowName: '_self', presentationStyle: 'fullscreen' });
    return;
  }
  window.location.href = url;
};

/**
 * Consume a Supabase OAuth redirect URL (the one the system browser handed us
 * via the appUrlOpen deep link). Parses the fragment, applies the session,
 * and returns the resolved Supabase user. Throws on failure.
 */
export const applyOAuthRedirect = async (url) => {
  if (!url) throw new Error('Missing redirect URL');
  // The fragment may be either "#access_token=..." or "?code=..." depending
  // on Supabase flow type. We use the implicit flow → tokens in fragment.
  const u = new URL(url);
  const params = new URLSearchParams(
    (u.hash && u.hash.length > 1 ? u.hash.slice(1) : '') || u.search.slice(1),
  );
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  const errorDescription = params.get('error_description');
  if (errorDescription) throw new Error(decodeURIComponent(errorDescription));
  if (!access_token || !refresh_token) {
    throw new Error('OAuth redirect missing tokens');
  }
  const { data, error } = await supabase.auth.setSession({
    access_token,
    refresh_token,
  });
  if (error) throw error;
  const session = data?.session;
  if (!session) throw new Error('Supabase setSession returned no session');
  mirrorSessionToLegacy(session);
  return session.user;
};

/**
 * Fetch the currently authenticated user from the backend. The backend
 * verifies the Supabase JWT and returns the canonical MongoDB user row
 * (which is also where first-login upsert + email-merge happens).
 */
export const fetchCurrentUser = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) return null;
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) return null;
    if (!response.ok) return null;
    const user = await response.json();
    return user;
  } catch (_) {
    return null;
  }
};

/**
 * Sign out: tells Supabase to invalidate the session and clears local mirror.
 */
export const logout = async () => {
  try {
    await supabase.auth.signOut();
  } catch (_) {
    /* ignore network errors — Supabase still clears local storage */
  }
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
    localStorage.removeItem('foresight_auth');
    // Drop user-scoped cached data so the next account starts clean
    localStorage.removeItem('foresight_meets');
    localStorage.removeItem('foresight_purchase_status');
    localStorage.removeItem('foresight_purchase_data');
    localStorage.removeItem('foresight_sync_state');
  } catch (_) {
    /* ignore */
  }
};

/**
 * Tell the backend to migrate any orphan device-scoped meets to the freshly
 * authenticated user. Idempotent.
 */
export const linkDevice = async (deviceId) => {
  if (!deviceId) return null;
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return null;
  try {
    const response = await fetch(`${API_URL}/api/auth/link-device`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ device_id: deviceId }),
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (_) {
    return null;
  }
};

/**
 * Returns the cached user or null. Synchronous helper for first-render UI.
 */
export const getCachedUser = () => {
  try {
    const raw = localStorage.getItem(LEGACY_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

/**
 * Returns the cached Supabase access_token (used for native Bearer fallback).
 */
export const getSessionToken = () => {
  try {
    return localStorage.getItem(LEGACY_TOKEN_KEY);
  } catch (_) {
    return null;
  }
};
